import { useLocalization } from '@lib/localization';
import { Box, CardContent, FormControl, InputLabel, MenuItem, Paper, Select, Typography } from '@mui/material';
import { useUpdatePreferencesMutation } from '@store/api/authEndpoints';
import { type TeamRole } from '@store/api/teamEndpoints';
import { useAppSelector } from '@store/hooks';
import { CardDescription, CardHeader, CardTitle } from './Settings.styled';

const AUTOMATIC = 'auto';

const roleKeys: Record<TeamRole, string> = {
  MANAGER: 'Team_Role_Manager',
  INSPECTOR: 'Team_Role_Inspector',
  ANALYST: 'Team_Role_Analyst',
};

/**
 * Which team's app settings this user's SuPAR devices take, and whose brand the reports they
 * download carry. Left on Automatic it follows the user's teams — highest role first, then
 * the team joined first — so joining or leaving a team can move it; picking one keeps it
 * there until it is changed or the user leaves it. Only the user sees and sets it.
 *
 * The two sentences that say what it decides have keys of their own (`..._Reports`), apart
 * from the ones this card had before a report carried a brand: a package of a line without
 * the brand reads the same table, and must not speak of one.
 */
const DefaultTeamCard = () => {
  const { t } = useLocalization();
  const user = useAppSelector((state) => state.auth.user);
  // The resolved team lives on the session, which the write reads again by itself.
  const [updatePreferences, { isLoading }] = useUpdatePreferencesMutation();

  const teams = user?.teams ?? [];
  const defaultTeam = user?.defaultTeam ?? null;
  const teamName = (id?: string) => teams.find((team) => team.id === id)?.name ?? '';

  const value = defaultTeam?.source === 'manual' ? defaultTeam.id : AUTOMATIC;

  const choose = (next: string) => {
    if (next !== value) void updatePreferences({ defaultTeamId: next === AUTOMATIC ? null : next });
  };

  return (
    <Paper sx={{ mb: 2 }}>
      <CardHeader>
        <CardTitle variant="h6">{t('Settings_DefaultTeam')}</CardTitle>
        <CardDescription variant="body2">{t('Settings_DefaultTeam_Subtitle_Reports')}</CardDescription>
      </CardHeader>
      <CardContent style={{ padding: '24px' }}>
        {teams.length === 0 ? (
          <Typography variant="body2" color="text.secondary">{t('Settings_DefaultTeam_NoTeams_Reports')}</Typography>
        ) : (
          <FormControl fullWidth size="small">
            <InputLabel id="default-team-select-label">{t('Settings_DefaultTeam_Select')}</InputLabel>
            <Select
              labelId="default-team-select-label"
              value={value}
              label={t('Settings_DefaultTeam_Select')}
              disabled={isLoading}
              onChange={(event) => choose(event.target.value)}
              renderValue={(selected) => (selected === AUTOMATIC
                ? `${t('Settings_DefaultTeam_Automatic')} · ${teamName(defaultTeam?.id)}`
                : teamName(selected))}
            >
              <MenuItem value={AUTOMATIC}>
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{t('Settings_DefaultTeam_Automatic')}</Typography>
                  <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                    {t('Settings_DefaultTeam_Automatic_Help')}
                  </Typography>
                </Box>
              </MenuItem>
              {teams.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{team.name}</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: 12 }}>{t(roleKeys[team.role])}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </CardContent>
    </Paper>
  );
};

export default DefaultTeamCard;
